import { assignLocation } from './ccToEcRedirectNavigation';
import { IS_EC_RAIL } from './rail';

/**
 * CC → EC page redirects (ENG-12776).
 *
 * Every API call carries the app name. While the `cc-to-ec-page-redirects` flag is on and the app
 * runs on the CC rail, it also carries the current page path. The backend answers with HTTP 470 and
 * the EC page to open when the page has a migrated equivalent; the response interceptor turns that
 * into a full navigation. No page path is sent on the EC rail: there is nothing left to redirect
 * there.
 *
 * The flag lives in module state, fed by `CcToEcRedirectFlagSync`, because the axios layer runs
 * outside React and LaunchDarkly flags are only readable through hooks.
 */

// Mirrors EC_REDIRECT_STATUS in backend-api `common/ec_redirect/constants.py`.
export const EC_REDIRECT_STATUS = 470;
export const PAGE_PATH_HEADER = 'X-Page-Path';
export const PAGE_APP_HEADER = 'X-Page-App';
export const PAGE_APP_NAME = 'consumer-flow';

type LoaderHandler = () => void;

export interface EcRedirectCandidate {
  status: number;
  data?: unknown;
}

let isEnabled = false;
let loaderHandler: LoaderHandler | null = null;

export const setCcToEcRedirectEnabled = (enabled: boolean): void => {
  isEnabled = enabled;
};

export const isCcToEcRedirectEnabled = (): boolean => isEnabled;

export const setCcToEcRedirectLoaderHandler = (handler: LoaderHandler | null): void => {
  loaderHandler = handler;
};

export const getPagePathForRedirect = (): string =>
  `${window.location.pathname}${window.location.search}`;

export const toFullPageUrl = (redirectTo: string): string => redirectTo;

export const getCcToEcPageHeaders = (): Record<string, string> => ({
  [PAGE_APP_HEADER]: PAGE_APP_NAME,
  ...(isEnabled && !IS_EC_RAIL ? { [PAGE_PATH_HEADER]: getPagePathForRedirect() } : {}),
});

const readRedirectTarget = (response: EcRedirectCandidate | null | undefined): string | null => {
  if (response?.status !== EC_REDIRECT_STATUS) {
    return null;
  }

  const redirectTo = (response.data as { redirect_to?: unknown } | undefined)?.redirect_to;

  return typeof redirectTo === 'string' && redirectTo.length > 0 ? redirectTo : null;
};

/**
 * Turns a 470 response into a full navigation to the EC page.
 *
 * Returns a promise that never settles so the calling query sees neither CC data nor an error while
 * the browser navigates away; returns null for any other response so the caller proceeds as usual.
 */
export const interceptEcRedirect = (
  response: EcRedirectCandidate | null | undefined,
): Promise<never> | null => {
  const redirectTo = readRedirectTarget(response);

  if (redirectTo === null || redirectTo === getPagePathForRedirect()) {
    return null;
  }

  loaderHandler?.();
  assignLocation(toFullPageUrl(redirectTo));

  return new Promise<never>(() => {});
};
